import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useT } from "../shared"
import { Btn, Badge, TableWrap, Tabs, ExportMenu } from "../shared/components"
import {
  listAppraisalForms, getAppraisalForm, createAppraisalForm, updateAppraisalForm, deleteAppraisalForm,
  duplicateAppraisalForm,
  saveAppraisalItems, assignFromProgram, assignManual, importAppraisalSubjects,
  listAppraisalRequests, getAppraisalRequest, resendAppraisal, cancelAppraisal, setAppraisalLink, appraisalQr,
  myAppraisals, downloadAppraisalExport, downloadAppraisalPdf,
} from "../api/appraisals.api"
import { listEnrollments, listClassEnrollments } from "../api/programs.api"

/* ============================================================
   Penilaian Kinerja / PKWT — layar admin & trainer.
   Layar yang dibuka penilai ada di AppraisalReviewScreen.jsx.
   ============================================================ */

const LABEL_BAWAAN = ["Kurang", "Cukup", "Baik", "Sangat Baik", "Istimewa"]

function statusBadge(s) {
  if (s === "PENDING") return <Badge tone="warning">Menunggu</Badge>
  if (s === "SUBMITTED") return <Badge tone="success">Selesai</Badge>
  if (s === "CANCELLED") return <Badge tone="default">Dibatalkan</Badge>
  return null
}

const fmt = (d) => (d ? new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "—")

/**
 * Simpan blob sebagai berkas unduhan.
 *
 * Nama berkasnya ditentukan SERVER lewat Content-Disposition, dan itulah yang
 * dipakai peramban selama atribut download di sini dikosongkan — sehingga nama
 * berkas tidak perlu disusun dua kali di dua tempat yang bisa berbeda.
 */
async function unduh(promise, namaCadangan) {
  const res = await promise
  const url = URL.createObjectURL(res.blob)
  const a = document.createElement("a")
  a.href = url
  a.download = res.filename || namaCadangan
  a.click()
  URL.revokeObjectURL(url)
}

/** Butir yang ikut menentukan nilai akhir. */
const BERSKOR = ["SCALE", "CHOICE"]

/** Skor tertinggi satu butir — padanan maxSkorButir() di sisi server. */
function maxSkor(it, scaleMax) {
  if (it.type === "CHOICE") {
    const angka = (it.options || []).map((o) => Number(o?.score)).filter((v) => Number.isFinite(v))
    return angka.length ? Math.max(...angka) : 0
  }
  return it.type === "SCALE" ? scaleMax : 0
}

/**
 * Porsi tiap butir terhadap nilai akhir.
 *
 * Ditampilkan hidup-hidup di editor karena "bobot 3" di sebelah "bobot 4" tidak
 * memberi tahu siapa pun apa artinya. Padanan sisi server ada di
 * appraisal.service.js — keduanya harus menjawab sama.
 */
function porsi(items, scaleMax = 5) {
  const out = {}
  const ikut = items.map((it, idx) => ({ it, idx }))
    .filter(({ it }) => BERSKOR.includes(it.type) && maxSkor(it, scaleMax) > 0)
  const total = ikut.reduce((n, { it }) => n + Math.max(0, Number(it.weight) || 0), 0)
  for (const { it, idx } of ikut) {
    out[idx] = total > 0 ? Math.round((Math.max(0, Number(it.weight) || 0) / total) * 1000) / 10 : 0
  }
  return out
}

// ─── Layar utama ─────────────────────────────────────────────────────────────

export default function AppraisalScreen() {
  const { lang, role } = useApp()
  const id = lang === "id"
  const [tab, setTab] = React.useState(role === "admin" ? "forms" : "inbox")

  const tabs = role === "admin"
    ? [
      { id: "forms", label: id ? "Formulir" : "Forms" },
      { id: "requests", label: id ? "Penilaian" : "Appraisals" },
      { id: "inbox", label: id ? "Tugas saya" : "My queue" },
    ]
    : [{ id: "inbox", label: id ? "Tugas saya" : "My queue" }]

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">{id ? "Kepegawaian" : "HR"}</div>
          <h1 className="h1" style={{ marginTop: 6 }}>{id ? "Penilaian Kinerja" : "Performance Appraisal"}</h1>
          <div className="muted mt-1">
            {id
              ? "Susun formulir penilaian, lalu minta trainer atau atasan mengisinya."
              : "Build appraisal forms, then have a trainer or supervisor fill them in."}
          </div>
        </div>
      </div>

      <Tabs tabs={tabs} value={tab} onChange={setTab} />

      {tab === "forms" && role === "admin" && <DaftarFormulir lang={lang} />}
      {tab === "requests" && role === "admin" && <DaftarPenilaian lang={lang} />}
      {tab === "inbox" && <TugasSaya lang={lang} />}
    </div>
  )
}

// ─── Formulir ────────────────────────────────────────────────────────────────

function DaftarFormulir({ lang }) {
  const id = lang === "id"
  const [forms, setForms] = React.useState([])
  const [memuat, setMemuat] = React.useState(true)
  const [dibuka, setDibuka] = React.useState(null)

  const muat = React.useCallback(() => {
    setMemuat(true)
    listAppraisalForms().then(setForms).catch(() => setForms([])).finally(() => setMemuat(false))
  }, [])
  React.useEffect(() => { muat() }, [muat])

  async function buat() {
    const nama = window.prompt(id ? "Nama formulir:" : "Form name:", "Electronic Review Form")
    if (!nama?.trim()) return
    try {
      const f = await createAppraisalForm({ name: nama.trim(), scale_max: 5, scale_labels: LABEL_BAWAAN })
      muat()
      setDibuka(f.id)
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    }
  }

  if (dibuka) return <EditorFormulir formId={dibuka} lang={lang} onKembali={() => { setDibuka(null); muat() }} />

  return (
    <div className="col gap-3">
      <div className="row between mt-3">
        <span className="muted small">{forms.length} {id ? "formulir" : "forms"}</span>
        <Btn variant="primary" onClick={buat} icon={<Icon name="edit" size={14} />}>
          {id ? "Formulir baru" : "New form"}
        </Btn>
      </div>

      {memuat && <div className="tile tile-pad muted small">{id ? "Memuat…" : "Loading…"}</div>}

      {!memuat && forms.length === 0 && (
        <div className="tile tile-pad" style={{ textAlign: "center", padding: 48 }}>
          <Icon name="doc" size={40} color="var(--text-tertiary)" />
          <h3 className="h3 mt-3">{id ? "Belum ada formulir" : "No forms yet"}</h3>
          <p className="muted small mt-2">
            {id
              ? "Buat satu formulir berisi kriteria berbobot, lalu tugaskan ke peserta atau pegawai."
              : "Create a form with weighted criteria, then assign it to participants or employees."}
          </p>
        </div>
      )}

      {forms.map((f) => (
        <div key={f.id} className="tile tile-pad row between" style={{ alignItems: "center" }}>
          <div style={{ minWidth: 0 }}>
            <div className="row gap-2">
              <span style={{ fontWeight: 500 }}>{f.name}</span>
              {!f.is_active && <Badge tone="default">{id ? "Nonaktif" : "Inactive"}</Badge>}
            </div>
            <div className="tiny muted mt-1">
              {f.items.length} {id ? "butir" : "items"} · {f._count?.requests ?? 0} {id ? "penilaian" : "appraisals"}
            </div>
          </div>
          <div className="row gap-2">
            <Btn variant="secondary" onClick={() => setDibuka(f.id)}>{id ? "Buka" : "Open"}</Btn>
            <Btn variant="secondary" onClick={async () => {
              try {
                const baru = await duplicateAppraisalForm(f.id)
                muat()
                setDibuka(baru.id)
              } catch (e) {
                toast.error(e?.response?.data?.error || e.message)
              }
            }}>{id ? "Salin" : "Copy"}</Btn>
            {(f._count?.requests ?? 0) === 0 && (
              <Btn variant="secondary" onClick={async () => {
                if (!window.confirm(id ? `Hapus formulir "${f.name}"?` : `Delete form "${f.name}"?`)) return
                try { await deleteAppraisalForm(f.id); muat() }
                catch (e) { toast.error(e?.response?.data?.error || e.message) }
              }}>{id ? "Hapus" : "Delete"}</Btn>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

function EditorFormulir({ formId, lang, onKembali }) {
  const id = lang === "id"
  const [form, setForm] = React.useState(null)
  const [items, setItems] = React.useState([])
  const [labels, setLabels] = React.useState(LABEL_BAWAAN)
  const [menyimpan, setMenyimpan] = React.useState(false)
  const [galat, setGalat] = React.useState("")
  const [tugaskan, setTugaskan] = React.useState(false)
  const [sedangUnduh, setSedangUnduh] = React.useState(null)

  React.useEffect(() => {
    getAppraisalForm(formId).then((f) => {
      setForm(f)
      setItems(f.items.map((i) => ({ type: i.type, label: i.label, help_text: i.help_text, weight: i.weight, options: i.options || null })))
      setLabels(f.scale_labels?.length ? f.scale_labels : LABEL_BAWAAN.slice(0, f.scale_max))
    }).catch((e) => setGalat(e?.response?.data?.error || e.message))
  }, [formId])

  if (!form) return <div className="tile tile-pad muted small mt-3">{galat || (id ? "Memuat…" : "Loading…")}</div>

  const p = porsi(items, form.scale_max)
  const ubah = (i, patch) => setItems((arr) => arr.map((it, n) => (n === i ? { ...it, ...patch } : it)))
  const geser = (i, ke) => setItems((arr) => {
    const b = [...arr]
    const j = i + ke
    if (j < 0 || j >= b.length) return b
    ;[b[i], b[j]] = [b[j], b[i]]
    return b
  })

  async function simpan() {
    setMenyimpan(true)
    setGalat("")
    try {
      await updateAppraisalForm(formId, { name: form.name, intro: form.intro, scale_labels: labels })
      await saveAppraisalItems(formId, items.filter((i) => i.label.trim()))
      toast.success(id ? "Tersimpan" : "Saved")
    } catch (e) {
      setGalat(e?.response?.data?.error || e.message)
    } finally {
      setMenyimpan(false)
    }
  }

  return (
    <div className="col gap-3 mt-3">
      <div className="row between">
        <Btn variant="secondary" onClick={onKembali}>← {id ? "Semua formulir" : "All forms"}</Btn>
        <div className="row gap-2">
          <ExportMenu
            formats={["csv", "xlsx", "pdf"]}
            kindPrefix="penilaian"
            busyKind={sedangUnduh}
            lang={lang}
            label={id ? "Unduh" : "Download"}
            onSelect={async (kind) => {
              const format = kind.split("-").pop()
              setSedangUnduh(kind)
              try {
                await unduh(downloadAppraisalExport(formId, format), `penilaian.${format}`)
              } catch (e) {
                toast.error(e?.response?.data?.error || e.message)
              } finally {
                setSedangUnduh(null)
              }
            }}
          />
          <Btn variant="secondary" onClick={() => setTugaskan(true)}>{id ? "Tugaskan" : "Assign"}</Btn>
          <Btn variant="primary" onClick={simpan} disabled={menyimpan}>
            {menyimpan ? "…" : (id ? "Simpan" : "Save")}
          </Btn>
        </div>
      </div>

      {galat && <div className="tile tile-pad small" style={{ color: "var(--red-60)" }}>{galat}</div>}

      <div className="tile tile-pad col gap-3">
        <label className="field">
          <span className="field-label">{id ? "Nama formulir" : "Form name"}</span>
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">{id ? "Pengantar di layar sampul" : "Cover screen intro"}</span>
          <textarea className="input" rows={2} value={form.intro || ""}
            onChange={(e) => setForm({ ...form, intro: e.target.value })} />
        </label>
        <div>
          <span className="field-label">{id ? `Label skala 1–${form.scale_max}` : `Scale labels 1–${form.scale_max}`}</span>
          <div className="tiny muted" style={{ marginBottom: 8 }}>
            {id
              ? "Angka telanjang membuat dua penilai mengartikan \"3\" secara berbeda."
              : "Bare numbers make two reviewers read \"3\" differently."}
          </div>
          <div className="row gap-2" style={{ flexWrap: "wrap" }}>
            {Array.from({ length: form.scale_max }, (_, i) => (
              <input key={i} className="input" style={{ width: 120 }} value={labels[i] || ""}
                placeholder={`${i + 1}`}
                onChange={(e) => setLabels((l) => { const b = [...l]; b[i] = e.target.value; return b })} />
            ))}
          </div>
        </div>
      </div>

      <div className="tile">
        <div className="tile-header">
          <h3>{id ? "Butir penilaian" : "Appraisal items"}</h3>
          <span className="tile-header-sub small">{items.length}</span>
        </div>

        {items.map((it, i) => (
          <div key={i} style={{ padding: "14px 16px", borderTop: i > 0 ? "1px solid var(--border)" : "none" }}>
            <div className="row gap-2" style={{ alignItems: "flex-start" }}>
              <select className="input" style={{ width: 130 }} value={it.type}
                onChange={(e) => {
                  const tipe = e.target.value
                  ubah(i, {
                    type: tipe,
                    weight: tipe === "ESSAY" ? 0 : (it.weight || 1),
                    // Empat baris kosong bukan angka sembarang: penilaian kinerja
                    // lazimnya bertingkat lima, dan memulai dari nol baris membuat
                    // admin harus menebak apa yang diharapkan di sini.
                    options: tipe === "CHOICE"
                      ? (it.options?.length ? it.options : [
                        { label: "", score: 5 }, { label: "", score: 4 },
                        { label: "", score: 3 }, { label: "", score: 2 },
                        { label: "", score: 1 },
                      ])
                      : null,
                  })
                }}>
                <option value="SCALE">{id ? "Skala" : "Scale"}</option>
                <option value="CHOICE">{id ? "Pilihan" : "Choice"}</option>
                <option value="ESSAY">{id ? "Esai" : "Essay"}</option>
              </select>
              <input className="input" style={{ flex: 1 }} value={it.label}
                placeholder={id ? "Mis. Kedisiplinan" : "e.g. Discipline"}
                onChange={(e) => ubah(i, { label: e.target.value })} />
              {it.type !== "ESSAY" && (
                <div style={{ width: 130, flexShrink: 0 }}>
                  <input className="input" type="number" min={0} value={it.weight ?? 1}
                    onChange={(e) => ubah(i, { weight: Number(e.target.value) })} />
                  <div className="tiny muted mt-1" style={{ textAlign: "center" }}>
                    {p[i] ?? 0}% {id ? "dari nilai" : "of score"}
                  </div>
                </div>
              )}
              <div className="row gap-1" style={{ flexShrink: 0 }}>
                <button className="btn sm ghost icon-only" onClick={() => geser(i, -1)} title="↑">↑</button>
                <button className="btn sm ghost icon-only" onClick={() => geser(i, 1)} title="↓">↓</button>
                <button className="btn sm ghost icon-only" onClick={() => setItems((a) => a.filter((_, n) => n !== i))} title="×">×</button>
              </div>
            </div>
            <input className="input mt-2" value={it.help_text || ""}
              placeholder={id ? "Penjelasan untuk penilai (opsional)" : "Guidance for the reviewer (optional)"}
              onChange={(e) => ubah(i, { help_text: e.target.value })} />

            {it.type === "CHOICE" && (
              <PenyuntingPilihan
                opsi={it.options || []}
                lang={lang}
                onUbah={(opsi) => ubah(i, { options: opsi })}
              />
            )}
          </div>
        ))}

        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)" }}>
          <Btn variant="secondary" onClick={() => setItems((a) => [...a, { type: "SCALE", label: "", weight: 1 }])}>
            + {id ? "Tambah butir" : "Add item"}
          </Btn>
          <div className="tiny muted mt-2">
            {id
              ? "Bobot tidak perlu berjumlah 100 — persentasenya dihitung sendiri. Butir esai tidak ikut dinilai, dan butir berbobot 0 dicatat tanpa memengaruhi nilai."
              : "Weights need not add to 100 — percentages are derived. Essay items are not scored, and an item with weight 0 is recorded without affecting the score."}
          </div>
        </div>
      </div>

      {tugaskan && (
        <ModalTugaskan formId={formId} lang={lang} onTutup={() => setTugaskan(false)} />
      )}
    </div>
  )
}

/**
 * Penyunting pilihan untuk satu butir.
 *
 * Skor ditulis terpisah dari kalimatnya karena keduanya memang bisa berbeda:
 * urutan lima pernyataan tidak selalu berarti 5-4-3-2-1, dan formulir yang
 * dipindahkan dari kertas kadang punya tingkat yang bobotnya tidak berurutan.
 */
function PenyuntingPilihan({ opsi, lang, onUbah }) {
  const id = lang === "id"
  const ubahBaris = (i, patch) => onUbah(opsi.map((o, n) => (n === i ? { ...o, ...patch } : o)))

  return (
    <div className="mt-2" style={{ paddingLeft: 12, borderLeft: "2px solid var(--border)" }}>
      <div className="tiny muted" style={{ marginBottom: 6 }}>
        {id
          ? "Pilihan, urut dari terbaik. Skor tertinggi menentukan nilai penuh butir ini."
          : "Choices, best first. The highest score defines full marks for this item."}
      </div>
      {opsi.map((o, i) => (
        <div key={i} className="row gap-2" style={{ marginBottom: 6, alignItems: "center" }}>
          <input className="input" style={{ flex: 1 }} value={o.label || ""}
            placeholder={id ? `Pernyataan tingkat ${i + 1}` : `Level ${i + 1} statement`}
            onChange={(e) => ubahBaris(i, { label: e.target.value })} />
          <input className="input" type="number" style={{ width: 74 }} min={0}
            value={o.score ?? 0}
            onChange={(e) => ubahBaris(i, { score: Number(e.target.value) })} />
          <button className="btn sm ghost icon-only"
            onClick={() => onUbah(opsi.filter((_, n) => n !== i))} title="×">×</button>
        </div>
      ))}
      <button className="btn sm ghost"
        onClick={() => onUbah([...opsi, { label: "", score: Math.max(0, opsi.length ? Math.min(...opsi.map((x) => Number(x.score) || 0)) - 1 : 5) }])}>
        + {id ? "Tambah pilihan" : "Add choice"}
      </button>
    </div>
  )
}

// ─── Penugasan ───────────────────────────────────────────────────────────────

function ModalTugaskan({ formId, lang, onTutup }) {
  const id = lang === "id"
  const { currentProgramId, currentClass, currentClassId } = useApp()
  const [mode, setMode] = React.useState("program")
  const [enrollments, setEnrollments] = React.useState([])
  const [dipilih, setDipilih] = React.useState([])
  const [subjek, setSubjek] = React.useState([])
  const [namaBaru, setNamaBaru] = React.useState("")
  const [penilaiNama, setPenilaiNama] = React.useState("")
  const [penilaiEmail, setPenilaiEmail] = React.useState("")
  const [jabatanPenilai, setJabatanPenilai] = React.useState("Atasan Langsung")
  const [periode, setPeriode] = React.useState("")
  const [sibuk, setSibuk] = React.useState(false)
  const [hasil, setHasil] = React.useState(null)
  const [galat, setGalat] = React.useState("")
  const fileRef = React.useRef(null)

  React.useEffect(() => {
    if (mode !== "program" || !currentProgramId) return
    // Kelas yang sedang dipilih di bilah sisi menyempitkan daftarnya — tanpa
    // ini, menugaskan "dari peserta program" selalu menawarkan seluruh
    // program, dan angkatan Februari bisa tercentang bersama angkatan Mei
    // dalam satu penugasan tanpa admin menyadarinya.
    const janji = currentClassId
      ? listClassEnrollments(currentProgramId, currentClassId)
      : listEnrollments(currentProgramId)
    janji.then(setEnrollments).catch(() => setEnrollments([]))
  }, [mode, currentProgramId, currentClassId])

  async function kirimProgram() {
    setSibuk(true); setGalat(""); setHasil(null)
    try {
      const r = await assignFromProgram(formId, {
        program_id: currentProgramId,
        enrollment_ids: dipilih,
        reviewer_name: penilaiNama || null,
        reviewer_email: penilaiEmail || null,
        reviewer_role_label: jabatanPenilai || null,
        period_label: periode || null,
      })
      setHasil(r)
    } catch (e) {
      setGalat(e?.response?.data?.error || e.message)
    } finally { setSibuk(false) }
  }

  function tambahNama() {
    const n = namaBaru.trim()
    // Nama kosong dan nama yang sudah ada dilewati diam-diam: menekan Enter dua
    // kali tidak boleh menghasilkan dua penilaian untuk orang yang sama.
    if (!n || subjek.includes(n)) { setNamaBaru(""); return }
    setSubjek((s) => [...s, n])
    setNamaBaru("")
  }

  async function kirimManual() {
    setSibuk(true); setGalat(""); setHasil(null)
    try {
      const r = await assignManual(formId, {
        subjects: subjek.map((n) => ({ name: n })),
        reviewer_name: penilaiNama || null,
        reviewer_email: penilaiEmail || null,
        reviewer_role_label: jabatanPenilai || null,
        period_label: periode || null,
      })
      setHasil(r)
      setSubjek([])
    } catch (e) {
      setGalat(e?.response?.data?.error || e.message)
    } finally { setSibuk(false) }
  }

  async function unggah(file) {
    if (!file) return
    setSibuk(true); setGalat(""); setHasil(null)
    try {
      setHasil(await importAppraisalSubjects(formId, file, {
        reviewer_role_label: jabatanPenilai || null,
        period_label: periode || null,
      }))
    } catch (e) {
      setGalat(e?.response?.data?.error || e.message)
    } finally {
      setSibuk(false)
      if (fileRef.current) fileRef.current.value = ""
    }
  }

  return (
    <div className="modal-backdrop" onClick={onTutup}>
      <div className="modal-box" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{id ? "Tugaskan penilaian" : "Assign appraisal"}</span>
          <button className="modal-close" onClick={onTutup}>×</button>
        </div>
        <div className="modal-body col gap-3">
          <div className="row gap-2">
            <Btn variant={mode === "program" ? "primary" : "secondary"} onClick={() => setMode("program")}>
              {id ? "Peserta program" : "Program participants"}
            </Btn>
            <Btn variant={mode === "manual" ? "primary" : "secondary"} onClick={() => setMode("manual")}>
              {id ? "Ketik manual" : "Manual entry"}
            </Btn>
            <Btn variant={mode === "excel" ? "primary" : "secondary"} onClick={() => setMode("excel")}>
              {id ? "Pegawai dari Excel" : "Employees from Excel"}
            </Btn>
          </div>

          <div className="row gap-2">
            <label className="field" style={{ flex: 1 }}>
              <span className="field-label">{id ? "Jabatan penilai" : "Reviewer role"}</span>
              <input className="input" value={jabatanPenilai} onChange={(e) => setJabatanPenilai(e.target.value)} />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span className="field-label">{id ? "Periode" : "Period"}</span>
              <input className="input" value={periode} placeholder="Semester 1 2026"
                onChange={(e) => setPeriode(e.target.value)} />
            </label>
          </div>

          {/* Penilainya satu untuk seluruh kiriman, baik namanya diambil dari
              daftar peserta maupun diketik sendiri — jadi kolomnya dipakai
              bersama kedua mode itu. */}
          {mode !== "excel" && (
            <>
              <div className="row gap-2">
                <label className="field" style={{ flex: 1 }}>
                  <span className="field-label">{id ? "Nama penilai" : "Reviewer name"}</span>
                  <input className="input" value={penilaiNama} onChange={(e) => setPenilaiNama(e.target.value)} />
                </label>
                <label className="field" style={{ flex: 1 }}>
                  <span className="field-label">
                    {id ? "Email penilai (opsional)" : "Reviewer email (optional)"}
                  </span>
                  <input className="input" type="email" value={penilaiEmail}
                    placeholder="atasan@perusahaan.co.id"
                    onChange={(e) => setPenilaiEmail(e.target.value)} />
                </label>
              </div>

              <div className="tiny muted" style={{ lineHeight: 1.55 }}>
                {id
                  ? "Kosongkan emailnya bila Anda ingin mengirim tautannya sendiri lewat WhatsApp — tautannya muncul di sini setelah ditugaskan."
                  : "Leave the email blank to send the link yourself — links appear here once assigned."}
              </div>
            </>
          )}

          {mode === "program" ? (
            <>
              {currentClass && (
                <div className="tiny muted">
                  {id ? "Kelas" : "Class"}: {currentClass.name} — {id
                    ? "hanya peserta kelas ini yang ditampilkan."
                    : "only this class's participants are shown."}
                </div>
              )}
              <div className="tile" style={{ maxHeight: 220, overflowY: "auto" }}>
                {enrollments.length === 0 && <div className="tile-pad muted small">—</div>}
                {enrollments.map((e) => (
                  <label key={e.id} className="row gap-2" style={{ padding: "9px 14px", cursor: "pointer", alignItems: "center" }}>
                    <input type="checkbox" checked={dipilih.includes(e.id)}
                      onChange={(ev) => setDipilih((d) => ev.target.checked ? [...d, e.id] : d.filter((x) => x !== e.id))} />
                    <span className="small">{e.user?.full_name}</span>
                  </label>
                ))}
              </div>

              <Btn variant="primary" onClick={kirimProgram} disabled={sibuk || dipilih.length === 0 || !(penilaiEmail || penilaiNama)}>
                {sibuk ? "…" : (id ? `Kirim ke ${dipilih.length} peserta` : `Send for ${dipilih.length}`)}
              </Btn>
            </>
          ) : mode === "manual" ? (
            <>
              <label className="field">
                <span className="field-label">{id ? "Nama yang dinilai" : "Person being appraised"}</span>
                <div className="row gap-2">
                  <input className="input" style={{ flex: 1 }} value={namaBaru}
                    placeholder={id ? "Ketik nama lalu Enter…" : "Type a name, then Enter…"}
                    onChange={(e) => setNamaBaru(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter menambah nama, bukan mengirim borongan — orang
                      // mengetik beberapa nama berturut-turut di sini.
                      if (e.key === "Enter") { e.preventDefault(); tambahNama() }
                    }} />
                  <Btn variant="secondary" onClick={tambahNama} disabled={!namaBaru.trim()}>+</Btn>
                </div>
              </label>

              {subjek.length > 0 && (
                <div className="tile" style={{ maxHeight: 220, overflowY: "auto" }}>
                  {subjek.map((n) => (
                    <div key={n} className="row between gap-2"
                      style={{ padding: "8px 14px", alignItems: "center" }}>
                      <span className="small" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n}</span>
                      <button className="btn sm ghost" onClick={() => setSubjek((s) => s.filter((x) => x !== n))}>×</button>
                    </div>
                  ))}
                </div>
              )}

              <div className="tiny muted" style={{ lineHeight: 1.55 }}>
                {id
                  ? "Untuk orang yang tidak terdaftar sebagai peserta program — tidak perlu punya akun."
                  : "For people not enrolled in a program — no account needed."}
              </div>

              <Btn variant="primary" onClick={kirimManual} disabled={sibuk || subjek.length === 0 || !(penilaiEmail || penilaiNama)}>
                {sibuk ? "…" : (id ? `Kirim untuk ${subjek.length} orang` : `Send for ${subjek.length}`)}
              </Btn>
            </>
          ) : (
            <>
              <div className="small muted" style={{ lineHeight: 1.6 }}>
                {id
                  ? "Berkas XLSX/CSV berkolom: full_name, reviewer_email (wajib); email, position, department, contract_start, contract_end, reviewer_name (opsional)."
                  : "XLSX/CSV with columns: full_name, reviewer_email (required); email, position, department, contract_start, contract_end, reviewer_name (optional)."}
              </div>
              <input ref={fileRef} type="file" accept=".csv,.xlsx" style={{ display: "none" }}
                onChange={(e) => unggah(e.target.files?.[0])} />
              <Btn variant="primary" onClick={() => fileRef.current?.click()} disabled={sibuk}>
                {sibuk ? "…" : (id ? "Pilih berkas" : "Choose file")}
              </Btn>
            </>
          )}

          {galat && <div className="small" style={{ color: "var(--red-60)" }}>{galat}</div>}
          {hasil && (
            <div className="small">
              <div>
                {id
                  ? `${hasil.created ?? hasil.added ?? 0} penilaian dibuat${hasil.rejected ? `, ${hasil.rejected} baris dilewati.` : "."}`
                  : `${hasil.created ?? hasil.added ?? 0} created${hasil.rejected ? `, ${hasil.rejected} rows skipped.` : "."}`}
              </div>

              {hasil.links?.length > 0 && (
                <div className="tile mt-2" style={{ maxHeight: 200, overflowY: "auto" }}>
                  <div className="row between" style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)" }}>
                    <span className="tiny muted">{id ? "Tautan penilaian" : "Review links"}</span>
                    <button className="btn sm ghost" onClick={() => {
                      // Satu blok teks siap tempel ke WhatsApp: nama lalu tautannya,
                      // karena tautan tanpa nama tidak bisa dibedakan satu dari lain.
                      const teks = hasil.links.map((l) => `${l.name || "-"}: ${l.url}`).join("\n")
                      navigator.clipboard?.writeText(teks)
                      toast.success(id ? "Semua tautan disalin" : "All links copied")
                    }}>{id ? "Salin semua" : "Copy all"}</button>
                  </div>
                  {hasil.links.map((l) => (
                    <div key={l.id} className="row between gap-2" style={{ padding: "7px 12px", alignItems: "center" }}>
                      <span className="tiny" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {l.name || "—"}
                      </span>
                      <button className="btn sm ghost" onClick={() => {
                        navigator.clipboard?.writeText(l.url)
                        toast.success(id ? "Tautan disalin" : "Link copied")
                      }}>{id ? "Salin" : "Copy"}</button>
                    </div>
                  ))}
                </div>
              )}
              {hasil.errors?.length > 0 && (
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {hasil.errors.map((e, i) => (
                    <li key={i} className="tiny">{id ? "Baris" : "Row"} {e.row}: {e.error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Daftar penilaian ────────────────────────────────────────────────────────

function DaftarPenilaian({ lang }) {
  const id = lang === "id"
  const { currentProgramId, currentClass, currentClassId } = useApp()
  const [rows, setRows] = React.useState([])
  const [saring, setSaring] = React.useState("")
  const [detail, setDetail] = React.useState(null)
  const [qr, setQr] = React.useState(null)

  const muat = React.useCallback(() => {
    // Tanpa saringan program, daftar ini memuat penilaian SELURUH program yang
    // pernah dijalankan lembaga ini. Penilaian lepas yang tidak punya program
    // (atau tidak punya enrolmen sama sekali, untuk kelas) tetap ikut tampil —
    // lihat catatan di rute /requests.
    listAppraisalRequests({
      ...(saring ? { status: saring } : {}),
      ...(currentProgramId ? { program_id: currentProgramId } : {}),
      ...(currentClassId ? { class_id: currentClassId } : {}),
    }).then(setRows).catch(() => setRows([]))
  }, [saring, currentProgramId, currentClassId])
  React.useEffect(() => { muat() }, [muat])

  // Tautannya sudah ikut di data daftar, jadi tidak perlu satu permintaan
  // tambahan hanya untuk menyalin.
  function salinTautan(r) {
    if (!r.reviewUrl) return
    navigator.clipboard?.writeText(r.reviewUrl)
    toast.success(id ? "Tautan disalin" : "Link copied")
  }

  async function ubahTautan(r, aktif) {
    try {
      await setAppraisalLink(r.id, aktif)
      muat()
      toast.success(aktif
        ? (id ? "Tautan diaktifkan" : "Link enabled")
        : (id ? "Tautan dimatikan" : "Link disabled"))
    } catch (e) { toast.error(e?.response?.data?.error || e.message) }
  }

  return (
    <div className="col gap-3 mt-3">
      <div className="row between" style={{ alignItems: "center", flexWrap: "wrap" }}>
        <div className="row gap-2">
          {[["", id ? "Semua" : "All"], ["PENDING", id ? "Menunggu" : "Pending"], ["SUBMITTED", id ? "Selesai" : "Done"]].map(([v, l]) => (
            <Btn key={v} variant={saring === v ? "primary" : "secondary"} onClick={() => setSaring(v)}>{l}</Btn>
          ))}
        </div>
        {currentClass && <span className="tiny muted">{id ? "Kelas" : "Class"}: {currentClass.name}</span>}
      </div>

      <TableWrap>
        <table className="table table-compact">
          <thead>
            <tr>
              <th>{id ? "Nama" : "Name"}</th>
              <th>{id ? "Kelas" : "Class"}</th>
              <th>{id ? "Jabatan" : "Position"}</th>
              <th>{id ? "Penilai" : "Reviewer"}</th>
              <th>Status</th>
              <th>{id ? "Nilai" : "Score"}</th>
              <th>{id ? "Tautan" : "Link"}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="muted small" style={{ textAlign: "center", padding: 24 }}>—</td></tr>}
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={{ fontWeight: 500 }}>{r.subject?.name || "—"}</td>
                <td className="small muted">{r.className || "—"}</td>
                <td className="small muted">{r.subject?.position || "—"}</td>
                <td className="small">{r.reviewer?.name || r.reviewer?.email || "—"}</td>
                <td>{statusBadge(r.status)}</td>
                <td className="mono small">{r.finalScore != null ? r.finalScore : "—"}</td>
                <td>
                  {r.status === "SUBMITTED"
                    ? <span className="tiny muted">—</span>
                    : r.linkActive
                      ? <Badge tone="success">{id ? "Aktif" : "Active"}</Badge>
                      : <Badge tone="default">{id ? "Mati" : "Off"}</Badge>}
                </td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <button className="btn sm ghost" onClick={() => setDetail(r.id)}>{id ? "Lihat" : "View"}</button>
                  {r.status !== "SUBMITTED" && (
                    <>
                      <button className="btn sm ghost" onClick={() => salinTautan(r)}>{id ? "Salin tautan" : "Copy link"}</button>
                      <button className="btn sm ghost" onClick={async () => {
                        try { setQr(await appraisalQr(r.id)) }
                        catch (e) { toast.error(e?.response?.data?.error || e.message) }
                      }}>QR</button>
                      <button className="btn sm ghost" onClick={() => ubahTautan(r, !r.linkActive)}>
                        {r.linkActive ? (id ? "Matikan" : "Disable") : (id ? "Aktifkan" : "Enable")}
                      </button>
                      <button className="btn sm ghost" onClick={() =>
                        resendAppraisal(r.id).then(() => toast.success(id ? "Undangan dikirim ulang" : "Invite resent"))
                          .catch((e) => toast.error(e?.response?.data?.error || e.message))}>
                        {id ? "Kirim ulang" : "Resend"}
                      </button>
                      <button className="btn sm ghost" onClick={() => {
                        if (!window.confirm(id ? "Batalkan penilaian ini?" : "Cancel this appraisal?")) return
                        cancelAppraisal(r.id).then(muat)
                      }}>{id ? "Batalkan" : "Cancel"}</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>

      {detail && <ModalDetail rid={detail} lang={lang} onTutup={() => setDetail(null)} />}
      {qr && <ModalQr data={qr} lang={lang} onTutup={() => setQr(null)} />}
    </div>
  )
}

/**
 * QR berisi tautan penilaian.
 *
 * Dipakai saat atasannya ada di depan Anda: ia memindai dengan ponselnya
 * sendiri dan langsung mengisi, tanpa perlu menyalin alamat panjang atau
 * menunggu email sampai.
 *
 * Peringatannya tidak dihilangkan: QR ini memuat kunci, bukan alamat publik.
 * Siapa pun yang memotretnya bisa mengisi atas nama penilai yang dituju.
 */
function ModalQr({ data, lang, onTutup }) {
  const id = lang === "id"

  function cetak() {
    // Jendela terpisah, bukan window.print() halaman ini — yang tercetak
    // harus lembar QR-nya saja, bukan seluruh dasbor di belakangnya.
    const w = window.open("", "_blank", "width=520,height=680")
    if (!w) { toast.error(id ? "Izinkan pop-up untuk mencetak" : "Allow pop-ups to print"); return }
    const esc = (t) => String(t ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]))
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>${esc(data.subject || "Penilaian")}</title><style>
        body{font-family:system-ui,-apple-system,sans-serif;text-align:center;padding:32px;color:#0F172A}
        h1{font-size:17px;margin:0 0 2px} .sub{color:#64748B;font-size:12px;margin-bottom:20px}
        img{width:300px;height:300px} .url{font-size:10px;color:#64748B;word-break:break-all;margin-top:14px}
        .petunjuk{font-size:12px;margin-top:18px;color:#334155}
      </style></head><body>
        <h1>${esc(data.subject || "")}</h1>
        <div class="sub">${esc(data.formName || "")}</div>
        <img src="${data.qrPngDataUrl}">
        <div class="petunjuk">${id ? "Pindai untuk mengisi penilaian" : "Scan to fill in the appraisal"}</div>
        <div class="url">${esc(data.url)}</div>
      </body></html>`)
    w.document.close()
    w.focus()
    setTimeout(() => w.print(), 350)
  }

  return (
    <div className="modal-backdrop" onClick={onTutup}>
      <div className="modal-box" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{id ? "QR penilaian" : "Appraisal QR"}</span>
          <button className="modal-close" onClick={onTutup}>×</button>
        </div>
        <div className="modal-body" style={{ textAlign: "center" }}>
          <div style={{ fontWeight: 500 }}>{data.subject || "—"}</div>
          {data.reviewer && (
            <div className="tiny muted mt-1">{id ? "Penilai" : "Reviewer"}: {data.reviewer}</div>
          )}

          {!data.linkActive && (
            <div className="small mt-3" style={{ color: "var(--amber-60)" }}>
              {id
                ? "Tautan ini sedang mati — QR-nya tidak akan bisa dipakai sampai diaktifkan lagi."
                : "This link is disabled — the QR will not work until it is enabled again."}
            </div>
          )}

          <img src={data.qrPngDataUrl} alt="QR"
            style={{ width: 260, height: 260, margin: "16px auto 0", display: "block",
                     opacity: data.linkActive ? 1 : 0.35 }} />

          <div className="tiny muted mt-2" style={{ wordBreak: "break-all" }}>{data.url}</div>

          <div className="row gap-2 mt-4" style={{ justifyContent: "center" }}>
            <Btn variant="secondary" onClick={() => {
              navigator.clipboard?.writeText(data.url)
              toast.success(id ? "Tautan disalin" : "Link copied")
            }}>{id ? "Salin tautan" : "Copy link"}</Btn>
            <Btn variant="secondary" onClick={() => {
              const a = document.createElement("a")
              a.href = data.qrPngDataUrl
              a.download = `qr-${String(data.subject || "penilaian").replace(/[^\p{L}\p{N}]+/gu, "-")}.png`
              a.click()
            }}>{id ? "Unduh QR" : "Download QR"}</Btn>
            <Btn variant="primary" onClick={cetak}>{id ? "Cetak" : "Print"}</Btn>
          </div>

          <div className="tiny muted mt-3" style={{ lineHeight: 1.5 }}>
            {id
              ? "QR ini memuat kunci, bukan sekadar alamat. Siapa pun yang memotretnya bisa mengisi penilaian ini — jangan sebar di tempat umum."
              : "This QR carries a key, not just an address. Anyone who photographs it can fill in this appraisal."}
          </div>
        </div>
      </div>
    </div>
  )
}

function ModalDetail({ rid, lang, onTutup }) {
  const id = lang === "id"
  const [d, setD] = React.useState(null)
  React.useEffect(() => { getAppraisalRequest(rid).then(setD).catch(() => setD(null)) }, [rid])

  return (
    <div className="modal-backdrop" onClick={onTutup}>
      <div className="modal-box" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{id ? "Rincian penilaian" : "Appraisal detail"}</span>
          <div className="row gap-2" style={{ alignItems: "center" }}>
            {d?.status === "SUBMITTED" && (
              <Btn variant="secondary" onClick={async () => {
                try { await unduh(downloadAppraisalPdf(rid), "penilaian.pdf") }
                catch (e) { toast.error(e?.response?.data?.error || e.message) }
              }}>{id ? "Unduh PDF" : "Download PDF"}</Btn>
            )}
            <button className="modal-close" onClick={onTutup}>×</button>
          </div>
        </div>
        <div className="modal-body">
          {!d ? <div className="muted small">{id ? "Memuat…" : "Loading…"}</div> : (
            <div className="col gap-3">
              <div>
                <div style={{ fontSize: 20, fontWeight: 500 }}>{d.subject?.name}</div>
                <div className="small muted">
                  {[d.subject?.position, d.subject?.department].filter(Boolean).join(" · ") || "—"}
                </div>
                {(d.subject?.contractStartAt || d.subject?.contractEndAt) && (
                  <div className="tiny muted mt-1">
                    {id ? "Kontrak" : "Contract"}: {fmt(d.subject.contractStartAt)} → {fmt(d.subject.contractEndAt)}
                  </div>
                )}
              </div>

              <div className="row gap-3">
                <div><div className="tiny muted">{id ? "Penilai" : "Reviewer"}</div>
                  <div className="small">{d.reviewer?.name || d.reviewer?.email || "—"}</div></div>
                <div><div className="tiny muted">{id ? "Nilai akhir" : "Final score"}</div>
                  <div className="mono" style={{ fontSize: 20 }}>{d.finalScore != null ? d.finalScore : "—"}</div></div>
              </div>

              <div className="tile">
                {(d.form?.items || []).map((it, i) => {
                  const a = (d.answers || []).find((x) => x.itemId === it.id)
                  return (
                    <div key={it.id} style={{ padding: "11px 14px", borderTop: i > 0 ? "1px solid var(--border)" : "none" }}>
                      <div className="tiny muted">
                        {it.label}{it.type === "SCALE" ? ` · ${id ? "bobot" : "weight"} ${it.weight}` : ""}
                      </div>
                      <div className="small" style={{ marginTop: 3, fontWeight: 500 }}>
                        {it.type === "ESSAY"
                          ? (a?.text || (id ? "Belum diisi" : "Not answered"))
                          : it.type === "CHOICE"
                            ? ((it.options || []).find((o) => Number(o?.score) === a?.score)?.label
                              || (id ? "Belum dipilih" : "Not chosen"))
                            : (a?.score != null
                              ? `${a.score} — ${d.form.scaleLabels?.[a.score - 1] || ""}`
                              : (id ? "Belum dinilai" : "Not rated"))}
                      </div>
                      {a?.note && <div className="tiny muted mt-1">{a.note}</div>}
                    </div>
                  )
                })}
              </div>

              {d.overallNote && (
                <div>
                  <div className="tiny muted">{id ? "Catatan keseluruhan" : "Overall note"}</div>
                  <div className="small mt-1" style={{ lineHeight: 1.6 }}>{d.overallNote}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Antrean penilai berakun ─────────────────────────────────────────────────

function TugasSaya({ lang }) {
  const id = lang === "id"
  const [rows, setRows] = React.useState([])
  const [memuat, setMemuat] = React.useState(true)

  React.useEffect(() => {
    myAppraisals().then(setRows).catch(() => setRows([])).finally(() => setMemuat(false))
  }, [])

  if (memuat) return <div className="tile tile-pad muted small mt-3">{id ? "Memuat…" : "Loading…"}</div>

  if (rows.length === 0) {
    return (
      <div className="tile tile-pad mt-3" style={{ textAlign: "center", padding: 48 }}>
        <Icon name="check" size={36} color="var(--green-60)" />
        <h3 className="h3 mt-3">{id ? "Tidak ada yang menunggu" : "Nothing waiting"}</h3>
        <p className="muted small mt-2">
          {id ? "Semua penilaian yang ditugaskan kepada Anda sudah selesai." : "Every appraisal assigned to you is done."}
        </p>
      </div>
    )
  }

  return (
    <div className="tile mt-3">
      <div className="tile-header">
        <h3>{id ? "Menunggu penilaian Anda" : "Awaiting your review"}</h3>
        <span className="tile-header-sub mono">{rows.length}</span>
      </div>
      {rows.map((r, i) => (
        <div key={r.id} className="row between" style={{ padding: "14px 16px", borderTop: i > 0 ? "1px solid var(--border)" : "none", alignItems: "center" }}>
          <div style={{ minWidth: 0 }}>
            <div className="small" style={{ fontWeight: 500 }}>{r.subject?.name || "—"}</div>
            <div className="tiny muted mt-1">
              {r.formName}{r.periodLabel ? ` · ${r.periodLabel}` : ""}
              {r.dueAt ? ` · ${id ? "batas" : "due"} ${fmt(r.dueAt)}` : ""}
            </div>
          </div>
          <Btn variant="primary" onClick={() => { window.location.href = `/?review=${r.token || ""}` }}>
            {id ? "Nilai" : "Review"}
          </Btn>
        </div>
      ))}
    </div>
  )
}
